import { useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { approveSecretRequest, rejectSecretRequest } from '@/apps/apis/secrets';
import { SECRETS_QUERY_KEYS, useSecretRequests } from '@/apps/apis/secrets/hooks';
import type { SecretRequestItem, SecretRequestStatus, SecretsMe } from '@/apps/apis/secrets/types';
import { ROUTES } from '@/routes/routes';
import { asArray, errorStatus, readableError, sameEmail, SecretRequestsTable, SecretsGate } from './common';

const FILTERS: SecretRequestStatus[] = ['PENDING', 'APPROVED', 'REJECTED', 'CONFLICT'];

const SecretRequests = ({ me }: { me: SecretsMe }) => {
  const [status, setStatus] = useState<SecretRequestStatus>('PENDING');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requests = useSecretRequests(status);
  const queryClient = useQueryClient();

  const decide = async (item: SecretRequestItem, decision: 'approve' | 'reject') => {
    setBusyId(item.requestId);
    setError(null);
    try {
      await (decision === 'approve' ? approveSecretRequest(item.requestId) : rejectSecretRequest(item.requestId));
    } catch (caught) {
      setError(
        errorStatus(caught) === 409
          ? 'The secret changed since this request was made, so it could not be applied (conflict).'
          : readableError(caught, `Unable to ${decision} this request.`),
      );
    } finally {
      setBusyId(null);
      void queryClient.invalidateQueries({ queryKey: SECRETS_QUERY_KEYS.all });
    }
  };

  const renderActions = me.canManage
    ? (item: SecretRequestItem) =>
        item?.status === 'PENDING' && !sameEmail(item?.requesterEmail, me.email) ? (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              variant="success"
              disabled={busyId === item.requestId}
              onClick={() => void decide(item, 'approve')}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              disabled={busyId === item.requestId}
              onClick={() => void decide(item, 'reject')}
            >
              Reject
            </Button>
          </div>
        ) : null
    : undefined;

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-end gap-3 flex-wrap mb-3">
        <div>
          <h2 className="mb-1">Secret change requests</h2>
          <p className="text-muted mb-0">
            <Link to={ROUTES.secrets.list}>All secrets</Link>
          </p>
        </div>
        <Form.Group style={{ minWidth: 200 }}>
          <Form.Label className="small text-muted">Status</Form.Label>
          <Form.Select value={status} onChange={(event) => setStatus(event.target.value as SecretRequestStatus)}>
            {FILTERS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {requests.error ? (
        <Alert variant="danger">{readableError(requests.error, 'Unable to load requests.')}</Alert>
      ) : null}

      <Card>
        <Card.Body>
          {requests.isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <SecretRequestsTable items={asArray(requests.data)} me={me} showService renderActions={renderActions} />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

const SecretRequestsPage = () => <SecretsGate>{(me) => <SecretRequests me={me} />}</SecretsGate>;

export default SecretRequestsPage;
